import { useEffect, useState } from "react";
import { DepartmentResponseView } from "../components/DepartmentResponseView";
import { Layout } from "../components/Layout";
import { Panel } from "../components/Panel";
import { fetchRuntimeOwnerDepartment, type RuntimeDepartmentDetail } from "../lib/api/runtimeWorkspaceClient";
import { statusLabel } from "../lib/departmentWorkflow";
import { useWorkspaceLanguage, withLanguagePrefix } from "../lib/i18n";
import { readBilingualText } from "../lib/i18n/bilingualContent";
import { getPerspectiveDisplayName } from "../lib/valueWorkflow";
import type { Department, DepartmentResponse } from "../types";

interface DepartmentResponsesProps {
  departmentId: string;
}

function toDepartment(detail: RuntimeDepartmentDetail, language: "de" | "en"): Department {
  return {
    id: detail.department.id,
    workspaceId: detail.department.workspaceId,
    name: readBilingualText(detail.department.nameI18n ?? detail.department.name, language),
    participantEmail: detail.department.participantEmail ?? "",
    participantUserId: detail.department.participantUserId,
    status: detail.department.status as Department["status"],
    inviteToken: detail.department.inviteToken
  };
}

function toResponse(detail: RuntimeDepartmentDetail, language: "de" | "en"): DepartmentResponse | undefined {
  if (!detail.department.responseStatus) return undefined;
  return {
    id: `response-${detail.department.id}`,
    workspaceId: detail.department.workspaceId,
    departmentId: detail.department.id,
    responseStatus: detail.department.responseStatus as DepartmentResponse["responseStatus"],
    answers: detail.department.answersI18n?.[language] ?? detail.department.answers ?? {},
    submittedAt: detail.department.submittedAt,
    createdAt: detail.department.submittedAt ?? "",
    updatedAt: detail.department.submittedAt ?? ""
  };
}

export function DepartmentResponses({ departmentId }: DepartmentResponsesProps) {
  const language = useWorkspaceLanguage();
  const copy = language === "en" ? {
    eyebrow: "Department perspective",
    title: "View department perspective",
    description: "These answers were submitted by the invited department and flow into the shared Customer Value Evaluation.",
    loadError: "Department perspective could not be loaded.",
    loadingTitle: "Answers are loading",
    loadingBody: "Workspace data is loading...",
    errorTitle: "Department perspective could not be loaded",
    back: "Back to departments",
    departmentStatus: "Department status",
    answers: "Answers",
    noAnswers: "No answers submitted yet."
  } : {
    eyebrow: "Abteilungsperspektive",
    title: "Abteilungsperspektive ansehen",
    description: "Diese Antworten wurden von der eingeladenen Abteilung eingereicht und fließen in die gemeinsame Customer-Value-Auswertung ein.",
    loadError: "Abteilungsperspektive konnte nicht geladen werden.",
    loadingTitle: "Antworten werden geladen",
    loadingBody: "D1-Daten werden geladen...",
    errorTitle: "Abteilungsperspektive konnte nicht geladen werden",
    back: "Zurück zu den Abteilungen",
    departmentStatus: "Abteilungsstatus",
    answers: "Antworten",
    noAnswers: "Noch keine Antworten eingereicht."
  };
  const [detail, setDetail] = useState<RuntimeDepartmentDetail | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let isMounted = true;

    async function loadDepartment() {
      setIsLoading(true);
      setError(undefined);
      try {
        const nextDetail = await fetchRuntimeOwnerDepartment(departmentId);
        if (isMounted) setDetail(nextDetail);
      } catch (caught) {
        if (isMounted) setError(caught instanceof Error ? caught.message : copy.loadError);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void loadDepartment();

    return () => {
      isMounted = false;
    };
  }, [departmentId, copy.loadError]);

  const department = detail ? toDepartment(detail, language) : undefined;
  const response = detail ? toResponse(detail, language) : undefined;

  return (
    <Layout
      eyebrow={copy.eyebrow}
      title={copy.title}
      description={copy.description}
    >
      {isLoading ? <Panel title={copy.loadingTitle}><p>{copy.loadingBody}</p></Panel> : null}
      {error ? (
        <Panel title={copy.errorTitle} variant="accent">
          <p className="form-error" role="alert">{error}</p>
          <div className="hero-actions">
            <a className="button button-primary" href={withLanguagePrefix("/workspace/departments", language)}>
              {copy.back}
            </a>
          </div>
        </Panel>
      ) : null}
      {detail && department ? (
        <>
          <Panel title={getPerspectiveDisplayName(department, language)} variant="accent">
            <div className="data-list">
              <div>
                <span>Workspace</span>
                <strong>{detail.workspace.name}</strong>
              </div>
              <div>
                <span>{copy.departmentStatus}</span>
                <strong className={`status-badge status-${department.status}`}>{statusLabel(department.status, language)}</strong>
              </div>
            </div>
          </Panel>
          <Panel title={copy.answers}>
            {response ? (
              <DepartmentResponseView department={department} response={response} audience="owner" language={language} />
            ) : (
              <p>{copy.noAnswers}</p>
            )}
          </Panel>
          <div className="hero-actions">
            <a className="button button-primary" href={withLanguagePrefix("/workspace/departments", language)}>
              {copy.back}
            </a>
          </div>
        </>
      ) : null}
    </Layout>
  );
}
